import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bot, Play, Pause, TrendingUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Strategy {
  id: string;
  name: string;
  description: string;
  config: any;
  is_predefined: boolean;
}

interface DeployedInstance {
  id: string;
  strategy_name: string;
  status: string;
  execution_mode: string;
  created_at: string;
}

interface StrategyDeploymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
}

export const StrategyDeploymentModal = ({ open, onOpenChange, accountId }: StrategyDeploymentModalProps) => {
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [deployedStrategies, setDeployedStrategies] = useState<DeployedInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchStrategies = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('strategies', { method: 'GET' });
      if (error) throw error;
      if (data?.strategies) setStrategies(data.strategies);
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  };

  const fetchDeployedStrategies = async () => {
    if (!user || !accountId) return;
    try {
      // Find all strategy_instances tagged with this demo_account_id (stored in strategy_parameters)
      const { data: paramRows, error: paramErr } = await supabase
        .from('strategy_parameters')
        .select('strategy_instance_id')
        .eq('param_name', 'demo_account_id')
        .eq('param_value', accountId);

      if (paramErr) throw paramErr;

      const ids = (paramRows ?? []).map((p: any) => p.strategy_instance_id);
      if (ids.length === 0) {
        setDeployedStrategies([]);
        return;
      }

      const { data: instances, error: instErr } = await supabase
        .from('strategy_instances')
        .select('id, strategy_name, status, execution_mode, created_at')
        .in('id', ids)
        .eq('user_id', user.id)
        .neq('status', 'stopped')
        .order('created_at', { ascending: false });

      if (instErr) throw instErr;
      setDeployedStrategies((instances ?? []) as DeployedInstance[]);
    } catch (error) {
      console.error('Error fetching deployed strategies:', error);
    }
  };

  useEffect(() => {
    if (open && accountId) {
      setLoading(true);
      Promise.all([fetchStrategies(), fetchDeployedStrategies()]).finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountId]);

  const handleDeployStrategy = async (strategy: Strategy) => {
    if (!user) {
      toast({ title: 'Not signed in', description: 'Sign in to deploy strategies', variant: 'destructive' });
      return;
    }
    setBusyId(strategy.id);
    try {
      // 1. Create the strategy instance (the engine reads from this table)
      const { data: instance, error: instanceError } = await supabase
        .from('strategy_instances')
        .insert({
          user_id: user.id,
          strategy_name: strategy.name,
          status: 'running',
          execution_mode: 'demo',
          risk_mode: 'medium',
        } as any)
        .select('id')
        .single();
      if (instanceError) throw instanceError;

      // 2. Seed parameters (incl. demo_account_id linkage)
      const cfg = strategy.config ?? {};
      const params = [
        { strategy_instance_id: instance.id, param_name: 'symbol', param_value: String(cfg.symbol ?? 'EUR/USD') },
        { strategy_instance_id: instance.id, param_name: 'position_size', param_value: String(cfg.position_size ?? 0.1) },
        { strategy_instance_id: instance.id, param_name: 'timeframe', param_value: String(cfg.timeframe ?? '1h') },
        { strategy_instance_id: instance.id, param_name: 'sl', param_value: String(cfg.sl ?? 2) },
        { strategy_instance_id: instance.id, param_name: 'tp', param_value: String(cfg.tp ?? 3) },
        { strategy_instance_id: instance.id, param_name: 'account_mode', param_value: 'demo' },
        { strategy_instance_id: instance.id, param_name: 'demo_account_id', param_value: accountId },
      ];
      const { error: paramsError } = await supabase.from('strategy_parameters').insert(params);
      if (paramsError) throw paramsError;

      // 3. Seed zeroed performance row
      await supabase.from('strategy_performance').insert({
        strategy_instance_id: instance.id,
        total_trades: 0, winning_trades: 0, losing_trades: 0, win_rate: 0,
        total_profit: 0, total_loss: 0, net_profit: 0, max_drawdown: 0,
        profit_factor: 0, sharpe_ratio: 0,
      });

      toast({ title: 'Strategy deployed', description: `${strategy.name} is now running on this demo account` });
      await fetchDeployedStrategies();
    } catch (error: any) {
      console.error('Deploy error:', error);
      toast({
        title: 'Deployment failed',
        description: error?.message || 'Failed to deploy strategy',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleStrategy = async (instanceId: string, currentStatus: string) => {
    setBusyId(instanceId);
    try {
      const nextStatus = currentStatus === 'running' ? 'paused' : 'running';
      const { error } = await supabase
        .from('strategy_instances')
        .update({ status: nextStatus })
        .eq('id', instanceId);
      if (error) throw error;
      toast({
        title: nextStatus === 'running' ? 'Strategy resumed' : 'Strategy paused',
        description: `Status updated successfully`,
      });
      await fetchDeployedStrategies();
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error?.message || 'Failed to update strategy',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  const isStrategyDeployed = (strategyName: string) => {
    return deployedStrategies.some((d) => d.strategy_name === strategyName);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Strategy Management
          </DialogTitle>
          <DialogDescription>
            Deploy and manage trading strategies for this demo account
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Deployed Strategies */}
          {deployedStrategies.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Active Deployments
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {deployedStrategies.map((deployment) => {
                  const isRunning = deployment.status === 'running';
                  return (
                    <Card key={deployment.id} className="relative">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">{deployment.strategy_name}</CardTitle>
                          <Badge variant={isRunning ? 'default' : 'secondary'}>
                            {isRunning ? 'Running' : 'Paused'}
                          </Badge>
                        </div>
                        <CardDescription className="uppercase text-xs">
                          {deployment.execution_mode} mode
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button
                          size="sm"
                          variant={isRunning ? 'outline' : 'default'}
                          disabled={busyId === deployment.id}
                          onClick={() => handleToggleStrategy(deployment.id, deployment.status)}
                          className="flex items-center gap-2"
                        >
                          {isRunning ? (
                            <>
                              <Pause className="h-3 w-3" />
                              Pause
                            </>
                          ) : (
                            <>
                              <Play className="h-3 w-3" />
                              Resume
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Available Strategies */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Available Strategies</h3>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading strategies…</p>
            ) : strategies.length === 0 ? (
              <p className="text-sm text-muted-foreground">No strategies available.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {strategies.map((strategy) => {
                  const deployed = isStrategyDeployed(strategy.name);
                  return (
                    <Card key={strategy.id} className="relative">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">{strategy.name}</CardTitle>
                          <Badge variant={strategy.is_predefined ? 'secondary' : 'outline'}>
                            {strategy.is_predefined ? 'Predefined' : 'Custom'}
                          </Badge>
                        </div>
                        <CardDescription>{strategy.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button
                          size="sm"
                          disabled={deployed || busyId === strategy.id}
                          onClick={() => handleDeployStrategy(strategy)}
                          className="flex items-center gap-2"
                        >
                          <Bot className="h-3 w-3" />
                          {deployed ? 'Deployed' : busyId === strategy.id ? 'Deploying…' : 'Deploy'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
